import { useState, useEffect, useRef, useCallback } from 'react';
import {
  QUESTIONS, PHASE_CONFIG, GAME_DURATION, MAX_LIVES, ERRORS_PER_LIFE,
  BOOST_DURATION, SLOW_DURATION, QUIZ_DURATION, DODGE_POINTS,
  GamePhase, QuizQuestion,
  OBSTACLE_TYPES, TRASH_TYPES, ObstacleType, TrashType,
  getRandomQuestion,
} from '../data/cheoThuyenData';

const MUSIC_URL = 'https://storage.readdy-site.link/project_files/d941aef1-6212-4727-9a5e-0c38b2f72f78/ef74e02a-4a4d-4e20-9215-81bbe8caa705_chillrightsmusic-kungfu-cat-382799.mp3?v=016d652d777b0f4eb49033021fc598f5';

/* ─── TYPES ─── */
interface FloatObj {
  id: number;
  x: number; y: number;
  type: ObstacleType | TrashType;
  isTrash: boolean;
  vx: number;
  width: number; height: number;
  dead: boolean;
  scored: boolean;
  fogAlpha?: number;
}

interface PlayerState {
  x: number;
  score: number;
  lives: number;
  errors: number;
  boostTimer: number;
  slowTimer: number;
  hitFlash: number;
  paddleAngle: number;
  wakeParticles: { x: number; y: number; life: number }[];
  dead: boolean;
  quizActive: boolean;
}

interface QuizState {
  question: QuizQuestion;
  answered: boolean;
  selectedIdx: number | null;
  timeLeft: number;
  showResult: boolean;
  isCorrect: boolean | null;
}

interface GameState {
  p1: PlayerState;
  p2: PlayerState;
  objects1: FloatObj[];
  objects2: FloatObj[];
  phase: GamePhase;
  scrollY: number;
  frame: number;
  spawnTimer: number;
  objIdCounter: number;
  running: boolean;
  timeLeft: number;
  gameOver: boolean;
  winner: 'p1' | 'p2' | 'draw' | null;
  usedQuestionIds1: Set<string>;
  usedQuestionIds2: Set<string>;
}

/* ─── CONSTANTS ─── */
const CANVAS_W = 960;
const CANVAS_H = 560;
const LANE_W = CANVAS_W / 2;
const BANK_W = 42;
const RIVER_W = LANE_W - BANK_W * 2;
const RIVER_Y0 = 80;
const BOAT_Y = CANVAS_H - 90;
const BOAT_BODY_W = 32;
const BOAT_BODY_H = 80;

/* ─── HELPERS ─── */
function makePlayer(x: number): PlayerState {
  return {
    x, score: 0, lives: MAX_LIVES, errors: 0,
    boostTimer: 0, slowTimer: 0, hitFlash: 0,
    paddleAngle: 0, wakeParticles: [], dead: false, quizActive: false,
  };
}

function makeObj(id: number, isTrash: boolean, phase: GamePhase): FloatObj {
  const cfg = PHASE_CONFIG[phase];
  // Thêm fog, dirt, oilspill vào obstacle pool từ phase 2 trở lên
  const obstaclePool = phase >= 2
    ? (['rock', 'branch', 'log', 'whirlpool', 'fog', 'dirt', 'oilspill'] as ObstacleType[])
    : (['rock', 'branch', 'log', 'whirlpool'] as ObstacleType[]);
  const types = isTrash ? TRASH_TYPES : obstaclePool;
  const type = types[Math.floor(Math.random() * types.length)] as ObstacleType & TrashType;
  const maxW = isTrash ? 30 : (type === 'log' ? 66 : type === 'fog' ? 80 : type === 'oilspill' ? 70 : 42);
  const maxH = isTrash ? 28 : (type === 'fog' ? 50 : type === 'oilspill' ? 40 : 38);
  const rx = 10 + Math.random() * (RIVER_W - maxW - 20);
  const vx = cfg.obstacleMove ? (Math.random() - 0.5) * 1.6 : 0;
  return { id, x: rx, y: -60, type, isTrash, vx, width: maxW, height: maxH, dead: false, scored: false, fogAlpha: 0.7 + Math.random() * 0.3 };
}

function pickQuestion(phase: GamePhase, usedIds: Set<string>): QuizQuestion {
  return getRandomQuestion(phase, usedIds);
}

/* ═══════════════════════════════
   DRAW HELPERS
═══════════════════════════════ */

function drawSharedBg(ctx: CanvasRenderingContext2D, scrollY: number) {
  const sky = ctx.createLinearGradient(0, 0, 0, RIVER_Y0);
  sky.addColorStop(0, '#87CEEB');
  sky.addColorStop(1, '#C5E8F7');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, CANVAS_W, RIVER_Y0);

  ctx.save();
  const sunGrd = ctx.createRadialGradient(CANVAS_W * 0.5, 22, 3, CANVAS_W * 0.5, 22, 26);
  sunGrd.addColorStop(0, '#FFF176');
  sunGrd.addColorStop(0.6, '#FFD54F');
  sunGrd.addColorStop(1, 'rgba(255,213,79,0)');
  ctx.fillStyle = sunGrd;
  ctx.beginPath(); ctx.arc(CANVAS_W * 0.5, 22, 26, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  const drawCloud = (cx: number, cy: number, r: number, alpha = 0.95) => {
    ctx.save(); ctx.globalAlpha = alpha; ctx.fillStyle = '#FFFFFF';
    [[-r * 0.5, r * 0.22, r * 0.6], [0, 0, r], [r * 0.55, r * 0.15, r * 0.65], [-r * 0.82, r * 0.32, r * 0.48]].forEach(([ox, oy, rad]) => {
      ctx.beginPath(); ctx.arc(cx + ox, cy + oy, rad, 0, Math.PI * 2); ctx.fill();
    });
    ctx.restore();
  };
  drawCloud(90, 32, 22);
  drawCloud(290, 20, 18, 0.85);
  drawCloud(590, 28, 20);
  drawCloud(800, 34, 17, 0.8);

  ctx.save();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 5;
  ctx.globalAlpha = 0.85;
  ctx.setLineDash([16, 10]);
  ctx.beginPath(); ctx.moveTo(LANE_W, 0); ctx.lineTo(LANE_W, CANVAS_H); ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 0.35;
  ctx.strokeStyle = '#B0BEC5';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(LANE_W, 0); ctx.lineTo(LANE_W, CANVAS_H); ctx.stroke();
  ctx.restore();
}

function drawLane(ctx: CanvasRenderingContext2D, offsetX: number, scrollY: number, teamColor: 'blue' | 'red') {
  const y0 = RIVER_Y0;
  const laneH = CANVAS_H - y0;

  const bankMainL = teamColor === 'blue' ? '#2E7D32' : '#1B5E20';
  const bankMainR = teamColor === 'blue' ? '#43A047' : '#388E3C';
  const bankEdge  = teamColor === 'blue' ? '#66BB6A' : '#4CAF50';

  const bankL = ctx.createLinearGradient(offsetX, 0, offsetX + BANK_W, 0);
  bankL.addColorStop(0, bankMainL); bankL.addColorStop(1, bankEdge);
  ctx.fillStyle = bankL; ctx.fillRect(offsetX, y0, BANK_W, laneH);

  const bankR = ctx.createLinearGradient(offsetX + LANE_W - BANK_W, 0, offsetX + LANE_W, 0);
  bankR.addColorStop(0, bankEdge); bankR.addColorStop(1, bankMainR);
  ctx.fillStyle = bankR; ctx.fillRect(offsetX + LANE_W - BANK_W, y0, BANK_W, laneH);

  const rg = ctx.createLinearGradient(0, y0, 0, CANVAS_H);
  if (teamColor === 'blue') {
    rg.addColorStop(0, '#29B6F6'); rg.addColorStop(0.45, '#0288D1'); rg.addColorStop(1, '#01579B');
  } else {
    rg.addColorStop(0, '#4FC3F7'); rg.addColorStop(0.45, '#0277BD'); rg.addColorStop(1, '#003d6b');
  }
  ctx.fillStyle = rg;
  ctx.fillRect(offsetX + BANK_W, y0, RIVER_W, laneH);

  ctx.save();
  ctx.globalAlpha = 0.12;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(offsetX + BANK_W + RIVER_W * 0.08, y0, RIVER_W * 0.26, laneH);
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 0.3;
  ctx.strokeStyle = '#81D4FA';
  ctx.lineWidth = 1.8;
  for (let i = 0; i < 9; i++) {
    const ry = y0 + ((scrollY * 1.8 + i * 58) % laneH);
    ctx.beginPath();
    ctx.moveTo(offsetX + BANK_W + 10, ry);
    ctx.bezierCurveTo(
      offsetX + BANK_W + RIVER_W * 0.28, ry - 6,
      offsetX + BANK_W + RIVER_W * 0.72, ry + 6,
      offsetX + BANK_W + RIVER_W - 10, ry,
    );
    ctx.stroke();
  }
  ctx.restore();

  for (let i = 0; i < 10; i++) {
    const ty = y0 + 12 + ((scrollY + i * 66) % (laneH - 20));
    drawTree(ctx, offsetX + 20, ty, 14);
    drawTree(ctx, offsetX + LANE_W - 22, ty + 28, 12);
  }

  ctx.save(); ctx.strokeStyle = '#1A237E'; ctx.lineWidth = 1.5;
  for (let b = 0; b < 2; b++) {
    const bx = offsetX + BANK_W * 0.6 + ((scrollY * 0.4 + b * 180 + (offsetX > 0 ? 90 : 0)) % (LANE_W * 0.85));
    const by = y0 - 20 - b * 10;
    ctx.beginPath();
    ctx.moveTo(bx - 6, by); ctx.quadraticCurveTo(bx - 3, by - 4, bx, by);
    ctx.moveTo(bx, by); ctx.quadraticCurveTo(bx + 3, by - 4, bx + 6, by);
    ctx.stroke();
  }
  ctx.restore();
}

function drawTree(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.fillStyle = '#1B5E20';
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#388E3C';
  ctx.beginPath(); ctx.arc(x + 3, y + 2, r * 0.68, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#66BB6A';
  ctx.beginPath(); ctx.arc(x - 3, y - 3, r * 0.5, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#4E342E';
  ctx.fillRect(x - 3, y + r - 2, 6, 9);
}

/* ─── DRAW: Boat ─── */
function drawBoat(
  ctx: CanvasRenderingContext2D,
  x: number, laneOffX: number,
  frame: number, flash: boolean,
  boostTimer: number, slowTimer: number,
  paddleAngle: number,
  wakeParticles: { x: number; y: number; life: number }[],
) {
  // Fix: add BANK_W so boat stays in river, not on shore
  const bx = x + laneOffX + BANK_W;
  const by = BOAT_Y;
  const bw = BOAT_BODY_W;
  const bh = BOAT_BODY_H;

  wakeParticles.forEach(p => {
    ctx.save();
    ctx.globalAlpha = Math.max(0, p.life / 50);
    ctx.fillStyle = boostTimer > 0 ? '#FFF9C4' : '#E1F5FE';
    ctx.beginPath(); ctx.arc(p.x + laneOffX, p.y, boostTimer > 0 ? 6 : 4, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  });

  ctx.save();
  ctx.globalAlpha = 0.2;
  ctx.fillStyle = '#B3E5FC';
  ctx.beginPath(); ctx.ellipse(bx, by + bh * 0.52, bw * 0.7, 10, 0, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  if (boostTimer > 0) {
    ctx.save();
    ctx.shadowBlur = 22; ctx.shadowColor = '#FFD600';
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = '#FFD600';
    ctx.beginPath(); ctx.ellipse(bx, by, bw * 1.1, bh * 0.55, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
  if (slowTimer > 0) {
    ctx.save();
    ctx.shadowBlur = 16; ctx.shadowColor = '#CE93D8';
    ctx.globalAlpha = 0.38;
    ctx.fillStyle = '#CE93D8';
    ctx.beginPath(); ctx.ellipse(bx, by, bw * 1.1, bh * 0.55, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  const rock = Math.sin(frame * 0.1) * 0.03;
  ctx.save();
  ctx.translate(bx, by);
  ctx.rotate(rock);

  if (flash) {
    ctx.save(); ctx.globalAlpha = 0.5; ctx.fillStyle = '#FF1744';
    ctx.fillRect(-bw, -bh * 0.6, bw * 2, bh * 1.2); ctx.restore();
  }

  ctx.fillStyle = 'rgba(0,0,0,0.13)';
  ctx.beginPath(); ctx.ellipse(0, bh * 0.52, bw * 0.85, 8, 0, 0, Math.PI * 2); ctx.fill();

  const hullGrd = ctx.createLinearGradient(-bw, 0, bw, 0);
  hullGrd.addColorStop(0, '#4E342E');
  hullGrd.addColorStop(0.4, '#795548');
  hullGrd.addColorStop(0.6, '#8D6E63');
  hullGrd.addColorStop(1, '#5D4037');
  ctx.fillStyle = hullGrd;
  ctx.beginPath();
  ctx.moveTo(0, -bh * 0.56);
  ctx.bezierCurveTo(bw * 0.78, -bh * 0.35, bw * 0.92, bh * 0.1, bw * 0.72, bh * 0.44);
  ctx.bezierCurveTo(bw * 0.4, bh * 0.58, -bw * 0.4, bh * 0.58, -bw * 0.72, bh * 0.44);
  ctx.bezierCurveTo(-bw * 0.92, bh * 0.1, -bw * 0.78, -bh * 0.35, 0, -bh * 0.56);
  ctx.closePath(); ctx.fill();

  const innerGrd = ctx.createLinearGradient(-bw * 0.58, 0, bw * 0.58, 0);
  innerGrd.addColorStop(0, '#6D4C41');
  innerGrd.addColorStop(0.5, '#A1887F');
  innerGrd.addColorStop(1, '#6D4C41');
  ctx.fillStyle = innerGrd;
  ctx.beginPath();
  ctx.moveTo(0, -bh * 0.44);
  ctx.bezierCurveTo(bw * 0.5, -bh * 0.26, bw * 0.62, bh * 0.06, bw * 0.47, bh * 0.34);
  ctx.bezierCurveTo(bw * 0.26, bh * 0.46, -bw * 0.26, bh * 0.46, -bw * 0.47, bh * 0.34);
  ctx.bezierCurveTo(-bw * 0.62, bh * 0.06, -bw * 0.5, -bh * 0.26, 0, -bh * 0.44);
  ctx.closePath(); ctx.fill();

  ctx.save(); ctx.globalAlpha = 0.18; ctx.strokeStyle = '#3E2723'; ctx.lineWidth = 1;
  for (let gi = -1; gi <= 1; gi++) {
    ctx.beginPath(); ctx.moveTo(gi * 7, -bh * 0.38); ctx.lineTo(gi * 8, bh * 0.3); ctx.stroke();
  }
  ctx.restore();

  ctx.save(); ctx.strokeStyle = '#FFCC80'; ctx.lineWidth = 1.8; ctx.globalAlpha = 0.9;
  const zw = bw * 0.55;
  ctx.beginPath();
  ctx.moveTo(-zw, bh * 0.04 - 5); ctx.lineTo(-zw * 0.5, bh * 0.04); ctx.lineTo(0, bh * 0.04 - 5);
  ctx.lineTo(zw * 0.5, bh * 0.04); ctx.lineTo(zw, bh * 0.04 - 5);
  ctx.stroke();
  ctx.strokeStyle = '#FFA726'; ctx.lineWidth = 1.2; ctx.globalAlpha = 0.7;
  ctx.beginPath();
  ctx.moveTo(-zw * 0.7, bh * 0.04 + 8); ctx.lineTo(-zw * 0.25, bh * 0.04 + 13);
  ctx.lineTo(zw * 0.25, bh * 0.04 + 8); ctx.lineTo(zw * 0.7, bh * 0.04 + 13);
  ctx.stroke();
  ctx.restore();

  ctx.save();
  ctx.strokeStyle = '#FFECB3'; ctx.lineWidth = 2; ctx.globalAlpha = 0.9;
  ctx.beginPath(); ctx.moveTo(-5, -bh * 0.5); ctx.lineTo(5, -bh * 0.5); ctx.stroke();
  ctx.fillStyle = '#FFD54F'; ctx.beginPath(); ctx.arc(0, -bh * 0.54, 3.5, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.translate(bw * 0.82, 0);
  ctx.rotate(paddleAngle);
  ctx.strokeStyle = '#8D6E63'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(0, -24); ctx.lineTo(0, 24); ctx.stroke();
  ctx.fillStyle = '#BCAAA4';
  ctx.beginPath(); ctx.ellipse(0, 28, 8, 13, 0, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.beginPath(); ctx.ellipse(-2, 26, 3, 8, -0.3, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  ctx.restore();
}

/* ─── OBSTACLE DRAW FUNCTIONS ─── */
function drawRock(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.save();
  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.22)';
  ctx.beginPath(); ctx.ellipse(x + 4, y + 18, 24, 7, 0.1, 0, Math.PI * 2); ctx.fill();

  // Main jagged rock body — pointy & rugged like underwater stone
  const pts: [number, number][] = [
    [x,      y - 22], // top sharp spike
    [x + 10, y - 14],
    [x + 22, y - 8],  // right jagged point
    [x + 18, y + 2],
    [x + 24, y + 8],  // right lower spike
    [x + 14, y + 14],
    [x,      y + 16],
    [x - 16, y + 13],
    [x - 23, y + 5],  // left spike
    [x - 18, y - 6],
    [x - 10, y - 16],
  ];
  const rockGrd = ctx.createLinearGradient(x - 20, y - 22, x + 20, y + 16);
  rockGrd.addColorStop(0,   '#B0BEC5');
  rockGrd.addColorStop(0.3, '#78909C');
  rockGrd.addColorStop(0.7, '#546E7A');
  rockGrd.addColorStop(1,   '#37474F');
  ctx.fillStyle = rockGrd;
  ctx.strokeStyle = '#263238'; ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  pts.slice(1).forEach(([px, py]) => ctx.lineTo(px, py));
  ctx.closePath(); ctx.fill(); ctx.stroke();

  // Dark crevice lines (rugged texture)
  ctx.save(); ctx.strokeStyle = 'rgba(30,40,50,0.38)'; ctx.lineWidth = 1; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(x - 6, y - 12); ctx.lineTo(x + 4, y + 2); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x + 8, y - 4);  ctx.lineTo(x + 14, y + 8); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x - 14, y + 1);  ctx.lineTo(x - 6, y + 11); ctx.stroke();
  ctx.restore();

  // Highlight on top spike
  ctx.save(); ctx.globalAlpha = 0.35; ctx.fillStyle = '#ECEFF1';
  ctx.beginPath(); ctx.ellipse(x - 4, y - 14, 6, 3, -0.5, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // Wet sheen at base
  ctx.save(); ctx.globalAlpha = 0.22; ctx.fillStyle = '#80DEEA';
  ctx.beginPath(); ctx.ellipse(x + 2, y + 13, 16, 4, 0, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
  ctx.restore();
}

function drawBranch(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.save();
  // Shadow on water
  ctx.fillStyle = 'rgba(0,0,0,0.14)';
  ctx.beginPath(); ctx.ellipse(x + 2, y + 10, 46, 6, 0, 0, Math.PI * 2); ctx.fill();

  // Main branch trunk
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  ctx.strokeStyle = '#4E342E'; ctx.lineWidth = 11;
  ctx.beginPath();
  ctx.moveTo(x - 44, y + 3);
  ctx.bezierCurveTo(x - 20, y - 5, x + 10, y + 5, x + 44, y - 2);
  ctx.stroke();
  // Branch bark highlight
  ctx.strokeStyle = '#8D6E63'; ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(x - 42, y + 2);
  ctx.bezierCurveTo(x - 18, y - 4, x + 9, y + 4, x + 42, y - 1);
  ctx.stroke();
  ctx.strokeStyle = '#BCAAA4'; ctx.lineWidth = 1.8; ctx.globalAlpha = 0.55;
  ctx.beginPath();
  ctx.moveTo(x - 38, y - 1);
  ctx.bezierCurveTo(x - 14, y - 7, x + 8, y + 1, x + 38, y - 4);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Small sub-branches
  ctx.strokeStyle = '#6D4C41'; ctx.lineWidth = 3.5;
  ctx.beginPath(); ctx.moveTo(x - 20, y); ctx.lineTo(x - 28, y - 12); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x + 10, y + 1); ctx.lineTo(x + 18, y - 10); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x + 32, y - 1); ctx.lineTo(x + 38, y - 11); ctx.stroke();

  // LEAVES on branch tips — bright green clusters to distinguish from log
  const drawLeaf = (lx: number, ly: number, angle: number, size: number, color: string) => {
    ctx.save();
    ctx.translate(lx, ly); ctx.rotate(angle);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(0, 0, size, size * 0.55, 0, 0, Math.PI * 2);
    ctx.fill();
    // vein
    ctx.strokeStyle = 'rgba(0,80,0,0.3)'; ctx.lineWidth = 0.8;
    ctx.beginPath(); ctx.moveTo(-size * 0.7, 0); ctx.lineTo(size * 0.7, 0); ctx.stroke();
    ctx.restore();
  };

  const leafColors = ['#388E3C', '#43A047', '#2E7D32', '#66BB6A', '#4CAF50'];
  const leafPositions: [number, number, number, number, number][] = [
    [x - 30, y - 14, -0.5, 9,  0],
    [x - 24, y - 18, 0.3,  8,  1],
    [x - 20, y - 11, -0.2, 7,  2],
    [x + 14, y - 12, 0.4,  9,  3],
    [x + 20, y - 17, -0.3, 8,  4],
    [x + 18, y - 9,  0.6,  7,  0],
    [x + 34, y - 13, -0.4, 9,  1],
    [x + 40, y - 17, 0.2,  8,  2],
    [x + 38, y - 9,  -0.1, 7,  3],
    [x - 2,  y - 8,  0.1,  8,  4],
    [x + 4,  y - 14, -0.3, 7,  0],
    [x - 8,  y - 12, 0.5,  7,  1],
  ];
  leafPositions.forEach(([lx, ly, ang, sz, ci]) => {
    drawLeaf(lx, ly, ang, sz, leafColors[ci]);
  });

  ctx.restore();
}

function drawLog(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.save();
  const LW = 76;
  const LH = 20;

  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.17)';
  ctx.beginPath(); ctx.ellipse(x + 3, y + LH + 5, LW * 0.5, 6, 0, 0, Math.PI * 2); ctx.fill();

  // Main log cylinder body
  const topGrd = ctx.createLinearGradient(x - LW * 0.5, y - LH, x - LW * 0.5, y + LH);
  topGrd.addColorStop(0,    '#A1887F');
  topGrd.addColorStop(0.25, '#8D6E63');
  topGrd.addColorStop(0.55, '#795548');
  topGrd.addColorStop(0.8,  '#6D4C41');
  topGrd.addColorStop(1,    '#4E342E');
  ctx.fillStyle = topGrd;
  ctx.strokeStyle = '#4E342E'; ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x - LW * 0.5 + 12, y - LH);
  ctx.lineTo(x + LW * 0.5 - 12, y - LH);
  ctx.bezierCurveTo(x + LW * 0.5 + 8, y - LH, x + LW * 0.5 + 8, y + LH, x + LW * 0.5 - 12, y + LH);
  ctx.lineTo(x - LW * 0.5 + 12, y + LH);
  ctx.bezierCurveTo(x - LW * 0.5 - 8, y + LH, x - LW * 0.5 - 8, y - LH, x - LW * 0.5 + 12, y - LH);
  ctx.closePath();
  ctx.fill(); ctx.stroke();

  // Wood grain lines — horizontal dark streaks along the log
  ctx.save(); ctx.globalAlpha = 0.32; ctx.lineCap = 'round';
  const grainLines = [
    { dy: -LH * 0.55, w: LW * 0.72, thick: 0.9 },
    { dy: -LH * 0.2,  w: LW * 0.88, thick: 1.2 },
    { dy:  LH * 0.1,  w: LW * 0.9,  thick: 0.9 },
    { dy:  LH * 0.45, w: LW * 0.75, thick: 1.1 },
  ];
  grainLines.forEach(g => {
    ctx.strokeStyle = '#3E2723'; ctx.lineWidth = g.thick;
    ctx.beginPath();
    ctx.moveTo(x - g.w * 0.5, y + g.dy);
    ctx.bezierCurveTo(x - g.w * 0.25, y + g.dy - 2, x + g.w * 0.25, y + g.dy + 2, x + g.w * 0.5, y + g.dy);
    ctx.stroke();
  });
  ctx.restore();

  // Bark knot/bump texture — small rough bumps
  ctx.save(); ctx.globalAlpha = 0.28;
  [[x - 16, y - 4], [x + 20, y + 3], [x - 32, y + 6], [x + 5, y - 8]].forEach(([kx, ky]) => {
    ctx.fillStyle = '#3E2723';
    ctx.beginPath(); ctx.ellipse(kx, ky, 5, 3, Math.random() * 0.8, 0, Math.PI * 2); ctx.fill();
  });
  ctx.restore();

  // Rough end rings (cross-section at both ends)
  const drawRing = (ex: number) => {
    ctx.save();
    ctx.strokeStyle = '#5D4037'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(ex, y, 9, LH, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = '#8D6E63'; ctx.lineWidth = 0.7; ctx.globalAlpha = 0.6;
    ctx.beginPath(); ctx.ellipse(ex, y, 5, LH * 0.55, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = '#6D4C41'; ctx.globalAlpha = 0.8;
    ctx.beginPath(); ctx.ellipse(ex, y, 3, LH * 0.3, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  };
  drawRing(x - LW * 0.5 + 9);
  drawRing(x + LW * 0.5 - 9);

  // Highlight streak
  ctx.save(); ctx.globalAlpha = 0.18; ctx.fillStyle = '#EFEBE9';
  ctx.beginPath(); ctx.ellipse(x, y - LH * 0.5, LW * 0.3, 4, 0, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  ctx.restore();
}

function drawWhirlpool(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  ctx.save();
  const r = 26;
  const og = ctx.createRadialGradient(x, y, 2, x, y, r + 10);
  og.addColorStop(0, 'rgba(1,87,155,0.5)'); og.addColorStop(1, 'rgba(1,87,155,0)');
  ctx.fillStyle = og; ctx.beginPath(); ctx.arc(x, y, r + 10, 0, Math.PI * 2); ctx.fill();
  const mg = ctx.createRadialGradient(x, y, 3, x, y, r);
  mg.addColorStop(0, '#01579B'); mg.addColorStop(0.5, '#0277BD'); mg.addColorStop(1, 'rgba(2,119,189,0.2)');
  ctx.fillStyle = mg; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  for (let ring = 0; ring < 3; ring++) {
    const radius = r * (0.28 + ring * 0.28);
    const startAngle = frame * 0.1 + ring * Math.PI * 0.65;
    ctx.strokeStyle = `rgba(100,220,255,${0.78 - ring * 0.16})`;
    ctx.lineWidth = 2.8 - ring * 0.5; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.arc(x, y, radius, startAngle, startAngle + Math.PI * 1.5); ctx.stroke();
  }
  ctx.fillStyle = '#01579B'; ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#001F3F'; ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

function drawBag(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  ctx.save();
  const bob = Math.sin(frame * 0.08) * 2;
  // Shadow on water
  ctx.fillStyle = 'rgba(0,0,0,0.11)';
  ctx.beginPath(); ctx.ellipse(x + 2, y + 17 + bob, 14, 4, 0, 0, Math.PI * 2); ctx.fill();

  // Plastic bag body — crinkled, semi-transparent white
  ctx.fillStyle = 'rgba(240,248,255,0.88)'; ctx.strokeStyle = '#90CAF9'; ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x - 13, y - 2 + bob);
  ctx.bezierCurveTo(x - 17, y + 5 + bob, x - 16, y + 15 + bob, x - 8, y + 18 + bob);
  ctx.bezierCurveTo(x - 3, y + 20 + bob, x + 3, y + 20 + bob, x + 9, y + 18 + bob);
  ctx.bezierCurveTo(x + 16, y + 15 + bob, x + 17, y + 5 + bob, x + 13, y - 2 + bob);
  ctx.closePath(); ctx.fill(); ctx.stroke();

  // Wrinkle / crinkle lines inside the bag
  ctx.save(); ctx.strokeStyle = 'rgba(100,180,255,0.4)'; ctx.lineWidth = 0.9; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(x - 6, y + 2 + bob); ctx.lineTo(x - 4, y + 14 + bob); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x + 4, y + 3 + bob);  ctx.lineTo(x + 6, y + 15 + bob); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x - 1, y + 1 + bob);  ctx.lineTo(x,     y + 16 + bob); ctx.stroke();
  ctx.restore();

  // Handles (plastic bag straps)
  ctx.strokeStyle = '#78BFE0'; ctx.lineWidth = 2; ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x - 9, y - 3 + bob);
  ctx.bezierCurveTo(x - 7, y - 9 + bob, x - 3, y - 13 + bob, x, y - 14 + bob);
  ctx.bezierCurveTo(x + 3, y - 13 + bob, x + 7, y - 9 + bob, x + 9, y - 3 + bob);
  ctx.stroke();

  // "Rác" label
  ctx.save(); ctx.font = 'bold 8px sans-serif'; ctx.fillStyle = 'rgba(30,100,160,0.7)'; ctx.textAlign = 'center';
  ctx.fillText('RÁC', x, y + 11 + bob); ctx.restore();

  // Glow ring (collectible)
  ctx.globalAlpha = 0.3 + 0.16 * Math.sin(frame * 0.13);
  ctx.strokeStyle = '#FFD600'; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.ellipse(x, y, 26, 22, 0, 0, Math.PI * 2); ctx.stroke();
  ctx.restore();
}

function drawBottle(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  ctx.save();
  const bob = Math.sin(frame * 0.09 + 1) * 2;
  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.11)';
  ctx.beginPath(); ctx.ellipse(x + 2, y + 20 + bob, 10, 4, 0, 0, Math.PI * 2); ctx.fill();

  // Bottle body — teal/cyan plastic with ribbing
  const btGrd = ctx.createLinearGradient(x - 10, 0, x + 10, 0);
  btGrd.addColorStop(0,   'rgba(100,220,240,0.55)');
  btGrd.addColorStop(0.4, 'rgba(200,245,255,0.85)');
  btGrd.addColorStop(0.7, 'rgba(100,220,240,0.65)');
  btGrd.addColorStop(1,   'rgba(0,180,210,0.6)');
  ctx.fillStyle = btGrd; ctx.strokeStyle = '#26C6DA'; ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x, y + 10 + bob, 10, 0, Math.PI);
  ctx.lineTo(x - 10, y - 5 + bob);
  ctx.bezierCurveTo(x - 10, y - 12 + bob, x - 6, y - 16 + bob, x - 5, y - 18 + bob);
  ctx.lineTo(x - 5, y - 22 + bob);
  ctx.lineTo(x + 5, y - 22 + bob);
  ctx.lineTo(x + 5, y - 18 + bob);
  ctx.bezierCurveTo(x + 6, y - 16 + bob, x + 10, y - 12 + bob, x + 10, y - 5 + bob);
  ctx.closePath(); ctx.fill(); ctx.stroke();

  // Ribbing grooves on bottle
  ctx.save(); ctx.strokeStyle = 'rgba(0,160,190,0.4)'; ctx.lineWidth = 1; ctx.lineCap = 'round';
  [-3, 3, 9].forEach(dy => {
    ctx.beginPath(); ctx.moveTo(x - 9, y + dy + bob); ctx.lineTo(x + 9, y + dy + bob); ctx.stroke();
  });
  ctx.restore();

  // Water/liquid inside
  ctx.save(); ctx.globalAlpha = 0.35; ctx.fillStyle = '#B2EBF2';
  ctx.beginPath();
  ctx.arc(x, y + 10 + bob, 7, 0, Math.PI);
  ctx.lineTo(x - 7, y - 1 + bob); ctx.lineTo(x + 7, y - 1 + bob);
  ctx.closePath(); ctx.fill();
  ctx.restore();

  // Cap — red plastic
  ctx.fillStyle = '#EF5350'; ctx.strokeStyle = '#C62828'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.roundRect(x - 5, y - 27 + bob, 10, 7, 2); ctx.fill(); ctx.stroke();
  // Cap ridges
  ctx.save(); ctx.strokeStyle = 'rgba(180,0,0,0.4)'; ctx.lineWidth = 0.7;
  [-3, 0, 3].forEach(dx => {
    ctx.beginPath(); ctx.moveTo(x + dx, y - 27 + bob); ctx.lineTo(x + dx, y - 20 + bob); ctx.stroke();
  });
  ctx.restore();

  // Highlight
  ctx.save(); ctx.globalAlpha = 0.4; ctx.fillStyle = '#FFFFFF';
  ctx.beginPath(); ctx.ellipse(x - 3, y + 2 + bob, 2.5, 8, -0.2, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // Glow
  ctx.globalAlpha = 0.28 + 0.16 * Math.sin(frame * 0.11 + 0.5);
  ctx.strokeStyle = '#FFD600'; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.ellipse(x, y, 24, 26, 0, 0, Math.PI * 2); ctx.stroke();
  ctx.restore();
}

function drawCan(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  ctx.save();
  const bob = Math.sin(frame * 0.1 + 2) * 2;
  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.11)';
  ctx.beginPath(); ctx.ellipse(x + 2, y + 19 + bob, 11, 4, 0, 0, Math.PI * 2); ctx.fill();

  // Can body — metallic crushed look
  const cg = ctx.createLinearGradient(x - 11, 0, x + 11, 0);
  cg.addColorStop(0,    '#C62828');
  cg.addColorStop(0.25, '#EF9A9A');
  cg.addColorStop(0.55, '#EF5350');
  cg.addColorStop(0.8,  '#E53935');
  cg.addColorStop(1,    '#B71C1C');
  ctx.fillStyle = cg; ctx.strokeStyle = '#B71C1C'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.roundRect(x - 11, y - 17 + bob, 22, 34, 3); ctx.fill(); ctx.stroke();

  // Can ribs (horizontal bands)
  ctx.save(); ctx.strokeStyle = 'rgba(150,0,0,0.35)'; ctx.lineWidth = 1;
  [-10, -5, 0, 5, 10].forEach(dy => {
    ctx.beginPath(); ctx.moveTo(x - 10, y + dy + bob); ctx.lineTo(x + 10, y + dy + bob); ctx.stroke();
  });
  ctx.restore();

  // Top & bottom rims
  ctx.fillStyle = '#9E9E9E';
  ctx.beginPath(); ctx.ellipse(x, y - 17 + bob, 11, 3.5, 0, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(x, y + 17 + bob, 11, 3.5, 0, 0, Math.PI * 2); ctx.fill();

  // Pull tab
  ctx.save(); ctx.strokeStyle = '#BDBDBD'; ctx.lineWidth = 1.5; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.arc(x + 3, y - 17 + bob, 4, Math.PI, 0); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x + 3, y - 21 + bob); ctx.lineTo(x + 6, y - 18 + bob); ctx.stroke();
  ctx.restore();

  // Metallic highlight
  ctx.save(); ctx.globalAlpha = 0.3; ctx.fillStyle = '#FFFFFF';
  ctx.beginPath(); ctx.ellipse(x - 4, y + bob, 3, 12, 0, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // Dent (crushed effect)
  ctx.save(); ctx.globalAlpha = 0.2; ctx.fillStyle = '#880000';
  ctx.beginPath(); ctx.ellipse(x + 5, y + 6 + bob, 4, 7, 0.4, 0, Math.PI * 2); ctx.fill();
  ctx.restore();

  // Glow
  ctx.globalAlpha = 0.32 + 0.16 * Math.sin(frame * 0.12 + 1);
  ctx.strokeStyle = '#FFD600'; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.ellipse(x, y, 22, 26, 0, 0, Math.PI * 2); ctx.stroke();
  ctx.restore();
}

function drawObstacle(ctx: CanvasRenderingContext2D, obj: FloatObj, laneOffX: number, frame: number) {
  const x = obj.x + BANK_W + laneOffX;
  const y = obj.y;
  ctx.save();
  if (obj.isTrash) {
    switch (obj.type as TrashType) {
      case 'bag':    drawBag(ctx, x, y, frame);    break;
      case 'bottle': drawBottle(ctx, x, y, frame); break;
      default:       drawCan(ctx, x, y, frame);    break;
    }
  } else {
    switch (obj.type as ObstacleType) {
      case 'rock':       drawRock(ctx, x, y);            break;
      case 'branch':     drawBranch(ctx, x, y);          break;
      case 'log':        drawLog(ctx, x, y);              break;
      case 'fog':        drawFog(ctx, x, y, frame, obj.fogAlpha); break;
      case 'dirt':       drawDirt(ctx, x, y, frame);     break;
      case 'oilspill':   drawOilSpill(ctx, x, y, frame); break;
      default:           drawWhirlpool(ctx, x, y, frame); break;
    }
  }
  ctx.restore();
}

function drawLaneHUD(ctx: CanvasRenderingContext2D, p: PlayerState, laneOffX: number) {
  for (let i = 0; i < MAX_LIVES; i++) {
    ctx.font = '18px serif';
    ctx.fillStyle = i < p.lives ? '#F44336' : 'rgba(189,189,189,0.5)';
    ctx.textAlign = 'left';
    ctx.fillText('♥', laneOffX + 10 + i * 22, RIVER_Y0 + 22);
  }
}

/* ─── NEW OBSTACLE DRAW FUNCTIONS ─── */

function drawFog(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number, alpha: number = 0.8) {
  ctx.save();
  const pulse = 0.75 + 0.25 * Math.sin(frame * 0.06);
  ctx.globalAlpha = alpha * pulse;

  // Main fog cloud body
  const fogGrd = ctx.createRadialGradient(x, y, 5, x, y, 42);
  fogGrd.addColorStop(0, 'rgba(220,230,240,0.95)');
  fogGrd.addColorStop(0.4, 'rgba(200,215,230,0.8)');
  fogGrd.addColorStop(0.75, 'rgba(180,200,220,0.5)');
  fogGrd.addColorStop(1, 'rgba(160,185,210,0)');
  ctx.fillStyle = fogGrd;
  ctx.beginPath(); ctx.ellipse(x, y, 42, 28, 0, 0, Math.PI * 2); ctx.fill();

  // Secondary puffs
  ctx.globalAlpha = alpha * pulse * 0.65;
  const fogGrd2 = ctx.createRadialGradient(x - 20, y + 5, 3, x - 20, y + 5, 28);
  fogGrd2.addColorStop(0, 'rgba(210,225,240,0.9)');
  fogGrd2.addColorStop(1, 'rgba(180,200,220,0)');
  ctx.fillStyle = fogGrd2;
  ctx.beginPath(); ctx.ellipse(x - 20, y + 5, 28, 18, 0.2, 0, Math.PI * 2); ctx.fill();

  const fogGrd3 = ctx.createRadialGradient(x + 22, y - 3, 3, x + 22, y - 3, 24);
  fogGrd3.addColorStop(0, 'rgba(215,228,242,0.85)');
  fogGrd3.addColorStop(1, 'rgba(180,200,220,0)');
  ctx.fillStyle = fogGrd3;
  ctx.beginPath(); ctx.ellipse(x + 22, y - 3, 24, 16, -0.15, 0, Math.PI * 2); ctx.fill();

  // Warning icon
  ctx.globalAlpha = alpha * 0.9;
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(80,100,120,0.85)';
  ctx.fillText('🌫️', x, y + 5);

  ctx.restore();
}

function drawDirt(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  ctx.save();
  const drift = Math.sin(frame * 0.05) * 3;

  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.12)';
  ctx.beginPath(); ctx.ellipse(x + 2, y + 18, 28, 7, 0, 0, Math.PI * 2); ctx.fill();

  // Dirt cloud / dust puff
  const dirtGrd = ctx.createRadialGradient(x, y, 4, x, y, 32);
  dirtGrd.addColorStop(0, 'rgba(180,140,90,0.92)');
  dirtGrd.addColorStop(0.35, 'rgba(160,120,70,0.75)');
  dirtGrd.addColorStop(0.7, 'rgba(140,100,55,0.45)');
  dirtGrd.addColorStop(1, 'rgba(120,85,40,0)');
  ctx.fillStyle = dirtGrd;
  ctx.beginPath(); ctx.ellipse(x + drift, y, 32, 22, 0, 0, Math.PI * 2); ctx.fill();

  // Dirt particles floating
  const particles = [
    { ox: -18, oy: -8, r: 5 }, { ox: 14, oy: -12, r: 4 },
    { ox: -8, oy: 10, r: 6 }, { ox: 20, oy: 5, r: 4 },
    { ox: 0, oy: -16, r: 3 }, { ox: -22, oy: 4, r: 4 },
  ];
  particles.forEach(({ ox, oy, r }) => {
    const pg = ctx.createRadialGradient(x + ox + drift * 0.5, y + oy, 0, x + ox + drift * 0.5, y + oy, r);
    pg.addColorStop(0, 'rgba(170,130,80,0.85)');
    pg.addColorStop(1, 'rgba(140,100,55,0)');
    ctx.fillStyle = pg;
    ctx.beginPath(); ctx.ellipse(x + ox + drift * 0.5, y + oy, r, r, Math.random() * 0.5, 0, Math.PI * 2); ctx.fill();
  });

  // Dirt label
  ctx.globalAlpha = 0.85;
  ctx.font = 'bold 13px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('💨', x, y + 6);

  ctx.restore();
}

function drawOilSpill(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  ctx.save();

  // Shadow
  ctx.fillStyle = 'rgba(0,0,0,0.15)';
  ctx.beginPath(); ctx.ellipse(x + 3, y + 22, 36, 8, 0, 0, Math.PI * 2); ctx.fill();

  // Oil spill base — dark iridescent patch on water
  const oilGrd = ctx.createRadialGradient(x, y + 5, 5, x, y + 5, 38);
  oilGrd.addColorStop(0, 'rgba(20,15,30,0.88)');
  oilGrd.addColorStop(0.3, 'rgba(40,20,60,0.75)');
  oilGrd.addColorStop(0.6, 'rgba(30,10,50,0.55)');
  oilGrd.addColorStop(1, 'rgba(10,5,20,0)');
  ctx.fillStyle = oilGrd;
  ctx.beginPath();
  ctx.ellipse(x, y + 5, 38, 22, 0, 0, Math.PI * 2);
  ctx.fill();

  // Iridescent rainbow sheen — multiple color layers
  const shimmer = (frame * 0.04) % (Math.PI * 2);
  const iridColors = [
    `rgba(${Math.floor(180 + 60 * Math.sin(shimmer))},50,200,0.35)`,
    `rgba(50,${Math.floor(180 + 60 * Math.sin(shimmer + 2))},220,0.3)`,
    `rgba(220,${Math.floor(150 + 60 * Math.sin(shimmer + 4))},50,0.28)`,
    `rgba(50,220,${Math.floor(150 + 60 * Math.sin(shimmer + 1))},0.25)`,
  ];
  iridColors.forEach((color, i) => {
    ctx.globalAlpha = 0.7;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(x + (i - 1.5) * 8, y + 3 + i * 3, 18 - i * 2, 10 - i, i * 0.3, 0, Math.PI * 2);
    ctx.fill();
  });

  // Oil droplets / blobs
  ctx.globalAlpha = 0.6;
  [[-22, 8], [18, -4], [-10, 16], [24, 12], [-28, 0]].forEach(([ox, oy]) => {
    const dg = ctx.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, 7);
    dg.addColorStop(0, 'rgba(60,20,80,0.8)');
    dg.addColorStop(1, 'rgba(20,5,40,0)');
    ctx.fillStyle = dg;
    ctx.beginPath(); ctx.ellipse(x + ox, y + oy, 7, 5, Math.random() * 0.5, 0, Math.PI * 2); ctx.fill();
  });

  // Warning icon
  ctx.globalAlpha = 0.9;
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('🛢️', x, y + 4);

  ctx.restore();
}

/* ═══════════════════════════════
   MAIN COMPONENT
═══════════════════════════════ */
export default function CheoThuyenGame({ onBack }: { onBack: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<GameState | null>(null);
  const keysRef = useRef<Set<string>>(new Set());
  const rafRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const quiz1TimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const quiz2TimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const quiz1Ref = useRef<QuizState | null>(null);
  const quiz2Ref = useRef<QuizState | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [quiz1Display, setQuiz1Display] = useState<QuizState | null>(null);
  const [quiz2Display, setQuiz2Display] = useState<QuizState | null>(null);
  const [result, setResult] = useState<{ winner: 'p1' | 'p2' | 'draw'; p1Score: number; p2Score: number; p1Lives: number; p2Lives: number } | null>(null);
  const [countdown, setCountdown] = useState(3);
  const [started, setStarted] = useState(false);
  const [timeDisplay, setTimeDisplay] = useState(GAME_DURATION);
  const [livesDisplay, setLivesDisplay] = useState({ p1: MAX_LIVES, p2: MAX_LIVES });
  const [scoreDisplay, setScoreDisplay] = useState({ p1: 0, p2: 0 });

  /* ─── Music ─── */
  useEffect(() => {
    if (!started || result) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      return;
    }
    if (!audioRef.current) {
      audioRef.current = new Audio(MUSIC_URL);
      audioRef.current.loop = true;
      audioRef.current.volume = 0.45;
    }
    audioRef.current.play().catch(() => {});
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [started, result]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const initState = useCallback((): GameState => ({
    p1: makePlayer(RIVER_W / 2 - BOAT_BODY_W / 2),
    p2: makePlayer(RIVER_W / 2 - BOAT_BODY_W / 2),
    objects1: [], objects2: [],
    phase: 1, scrollY: 0, frame: 0, spawnTimer: 0,
    objIdCounter: 0, running: true, timeLeft: GAME_DURATION,
    gameOver: false, winner: null,
    usedQuestionIds1: new Set<string>(),
    usedQuestionIds2: new Set<string>(),
  }), []);

  const endGame = useCallback((gs: GameState) => {
    gs.running = false; gs.gameOver = true;
    // Win condition: dead team loses; if time out compare lives first, then score
    const w = gs.p1.dead ? 'p2'
      : gs.p2.dead ? 'p1'
      : gs.p1.lives > gs.p2.lives ? 'p1'
      : gs.p2.lives > gs.p1.lives ? 'p2'
      : gs.p1.score > gs.p2.score ? 'p1'
      : gs.p2.score > gs.p1.score ? 'p2'
      : 'draw';
    gs.winner = w;
    setResult({ winner: w, p1Score: gs.p1.score, p2Score: gs.p2.score, p1Lives: gs.p1.lives, p2Lives: gs.p2.lives });
    cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (quiz1TimerRef.current) clearInterval(quiz1TimerRef.current);
    if (quiz2TimerRef.current) clearInterval(quiz2TimerRef.current);
  }, []);

  const resolveQuiz = useCallback((playerIdx: 1 | 2, idx: number) => {
    const gs = stateRef.current;
    if (!gs) return;
    const quizRef    = playerIdx === 1 ? quiz1Ref : quiz2Ref;
    const quizTRef   = playerIdx === 1 ? quiz1TimerRef : quiz2TimerRef;
    const setDisplay = playerIdx === 1 ? setQuiz1Display : setQuiz2Display;
    const p          = playerIdx === 1 ? gs.p1 : gs.p2;

    if (!quizRef.current) return;
    const question = quizRef.current.question;
    if (quizTRef.current) clearInterval(quizTRef.current);

    const correct = idx === question.correct;

    if (correct) {
      p.score += question.points;
      p.boostTimer = BOOST_DURATION;
    } else {
      p.score = Math.max(0, p.score - Math.floor(question.points / 2));
      p.slowTimer = SLOW_DURATION;
      p.errors++;
      if (p.errors >= ERRORS_PER_LIFE) { p.errors = 0; p.lives--; p.hitFlash = 20; }
      if (p.lives <= 0) p.dead = true;
    }

    const resultState: QuizState = {
      ...quizRef.current,
      answered: true,
      selectedIdx: idx,
      showResult: true,
      isCorrect: correct,
    };
    quizRef.current = resultState;
    setDisplay({ ...resultState });

    setScoreDisplay(prev => ({ ...prev, [`p${playerIdx}`]: p.score }));
    setLivesDisplay(prev => ({ ...prev, [`p${playerIdx}`]: p.lives }));

    const delay = correct ? 1500 : 3000;
    setTimeout(() => {
      quizRef.current = null;
      p.quizActive = false;
      setDisplay(null);
      if (gs.p1.dead || gs.p2.dead) endGame(gs);
    }, delay);
  }, [endGame]);

  const triggerQuiz = useCallback((playerIdx: 1 | 2, gs: GameState) => {
    const p = playerIdx === 1 ? gs.p1 : gs.p2;
    if (p.quizActive) return;
    p.quizActive = true;

    const usedIds = playerIdx === 1 ? gs.usedQuestionIds1 : gs.usedQuestionIds2;
    const question = pickQuestion(gs.phase, usedIds);
    usedIds.add(question.id);

    const quizState: QuizState = { question, answered: false, selectedIdx: null, timeLeft: QUIZ_DURATION, showResult: false, isCorrect: null };
    const quizRef    = playerIdx === 1 ? quiz1Ref : quiz2Ref;
    const quizTRef   = playerIdx === 1 ? quiz1TimerRef : quiz2TimerRef;
    const setDisplay = playerIdx === 1 ? setQuiz1Display : setQuiz2Display;

    quizRef.current = quizState;
    setDisplay({ ...quizState });

    if (quizTRef.current) clearInterval(quizTRef.current);
    quizTRef.current = setInterval(() => {
      if (!quizRef.current || quizRef.current.answered) { clearInterval(quizTRef.current!); return; }
      quizRef.current.timeLeft--;
      setDisplay(prev => prev ? { ...prev, timeLeft: quizRef.current?.timeLeft ?? 0 } : null);
      if (quizRef.current.timeLeft <= 0) {
        clearInterval(quizTRef.current!);
        resolveQuiz(playerIdx, -1);
      }
    }, 1000);
  }, [resolveQuiz]);

  const spawnObjects = useCallback((gs: GameState) => {
    gs.spawnTimer++;
    const cfg = PHASE_CONFIG[gs.phase];
    if (gs.spawnTimer < cfg.spawnInterval) return;
    gs.spawnTimer = 0;
    const isTrash = Math.random() < 0.38;
    gs.objects1.push(makeObj(gs.objIdCounter++, isTrash, gs.phase));
    const obj2 = makeObj(gs.objIdCounter++, isTrash, gs.phase);
    obj2.x = 10 + Math.random() * (RIVER_W - obj2.width - 20);
    gs.objects2.push(obj2);
  }, []);

  const checkHit = useCallback((boatX: number, obj: FloatObj) => {
    return (
      Math.abs(boatX - obj.x) < (BOAT_BODY_W * 0.75 + obj.width * 0.35) &&
      Math.abs(BOAT_Y - obj.y) < (BOAT_BODY_H * 0.48 + obj.height * 0.4)
    );
  }, []);

  const gameLoop = useCallback(() => {
    const gs = stateRef.current;
    const canvas = canvasRef.current;
    if (!gs || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (gs.running && !gs.gameOver) {
      gs.frame++;
      gs.scrollY = (gs.scrollY + PHASE_CONFIG[gs.phase].boatBaseSpeed) % (CANVAS_H - RIVER_Y0);

      const elapsed = GAME_DURATION - gs.timeLeft;
      gs.phase = elapsed < 60 ? 1 : elapsed < 150 ? 2 : elapsed < 240 ? 3 : 4;

      const moveH = 6;

      if (!gs.p1.dead) {
        if (keysRef.current.has('a') || keysRef.current.has('A'))
          gs.p1.x = Math.max(0, gs.p1.x - moveH);
        if (keysRef.current.has('d') || keysRef.current.has('D'))
          gs.p1.x = Math.min(RIVER_W - BOAT_BODY_W, gs.p1.x + moveH);
      }

      if (!gs.p2.dead) {
        if (keysRef.current.has('ArrowLeft'))
          gs.p2.x = Math.max(0, gs.p2.x - moveH);
        if (keysRef.current.has('ArrowRight'))
          gs.p2.x = Math.min(RIVER_W - BOAT_BODY_W, gs.p2.x + moveH);
      }

      gs.p1.paddleAngle = Math.sin(gs.frame * 0.17) * 0.55;
      gs.p2.paddleAngle = Math.sin(gs.frame * 0.17 + 1.2) * 0.55;

      const objSpeed = PHASE_CONFIG[gs.phase].obstacleSpeed;
      const canMove  = PHASE_CONFIG[gs.phase].obstacleMove;

      for (const obj of gs.objects1) {
        obj.y += objSpeed;
        if (canMove) obj.x = Math.max(0, Math.min(RIVER_W - obj.width, obj.x + obj.vx));
        if (!obj.dead && !gs.p1.dead) {
          if (checkHit(gs.p1.x, obj)) {
            // Always mark dead on hit to prevent objects passing through silently
            obj.dead = true;
            if (!gs.p1.quizActive) {
              if (obj.isTrash) {
                triggerQuiz(1, gs);
              } else {
                gs.p1.slowTimer = SLOW_DURATION; gs.p1.hitFlash = 20;
                gs.p1.errors++; gs.p1.score = Math.max(0, gs.p1.score - 8);
                if (gs.p1.errors >= ERRORS_PER_LIFE) { gs.p1.errors = 0; gs.p1.lives--; if (gs.p1.lives <= 0) gs.p1.dead = true; }
              }
            }
          } else if (!obj.isTrash && !obj.scored && obj.y > BOAT_Y + BOAT_BODY_H) {
            obj.scored = true;
            gs.p1.score += DODGE_POINTS;
          }
        }
      }
      gs.objects1 = gs.objects1.filter(o => o.y < CANVAS_H + 90);

      for (const obj of gs.objects2) {
        obj.y += objSpeed;
        if (canMove) obj.x = Math.max(0, Math.min(RIVER_W - obj.width, obj.x + obj.vx));
        if (!obj.dead && !gs.p2.dead) {
          if (checkHit(gs.p2.x, obj)) {
            obj.dead = true;
            if (!gs.p2.quizActive) {
              if (obj.isTrash) {
                triggerQuiz(2, gs);
              } else {
                gs.p2.slowTimer = SLOW_DURATION; gs.p2.hitFlash = 20;
                gs.p2.errors++; gs.p2.score = Math.max(0, gs.p2.score - 8);
                if (gs.p2.errors >= ERRORS_PER_LIFE) { gs.p2.errors = 0; gs.p2.lives--; if (gs.p2.lives <= 0) gs.p2.dead = true; }
              }
            }
          } else if (!obj.isTrash && !obj.scored && obj.y > BOAT_Y + BOAT_BODY_H) {
            obj.scored = true;
            gs.p2.score += DODGE_POINTS;
          }
        }
      }
      gs.objects2 = gs.objects2.filter(o => o.y < CANVAS_H + 90);

      [gs.p1, gs.p2].forEach(p => {
        if (gs.frame % 4 === 0) {
          const spread = p.boostTimer > 0 ? 8 : 4;
          p.wakeParticles.push({
            x: p.x + BANK_W + (Math.random() - 0.5) * spread,
            y: BOAT_Y + BOAT_BODY_H * 0.5,
            life: 50,
          });
        }
        p.wakeParticles.forEach(wp => { wp.y += 2.5; wp.life--; });
        p.wakeParticles = p.wakeParticles.filter(wp => wp.life > 0);
        if (p.boostTimer > 0) p.boostTimer--;
        if (p.slowTimer > 0) p.slowTimer--;
        if (p.hitFlash > 0) p.hitFlash--;
      });

      spawnObjects(gs);

      if (gs.p1.dead || gs.p2.dead) { endGame(gs); return; }

      if (gs.frame % 12 === 0) {
        setScoreDisplay({ p1: gs.p1.score, p2: gs.p2.score });
        setLivesDisplay({ p1: gs.p1.lives, p2: gs.p2.lives });
      }
    }

    /* ── RENDER ── */
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    drawSharedBg(ctx, gs.scrollY);
    drawLane(ctx, 0, gs.scrollY, 'blue');
    drawLane(ctx, LANE_W, gs.scrollY, 'red');

    gs.objects1.forEach(o => { if (!o.dead) drawObstacle(ctx, o, 0, gs.frame); });
    gs.objects2.forEach(o => { if (!o.dead) drawObstacle(ctx, o, LANE_W, gs.frame); });

    if (!gs.p1.dead) drawBoat(ctx, gs.p1.x, 0, gs.frame, gs.p1.hitFlash > 0, gs.p1.boostTimer, gs.p1.slowTimer, gs.p1.paddleAngle, gs.p1.wakeParticles);
    if (!gs.p2.dead) drawBoat(ctx, gs.p2.x, LANE_W, gs.frame, gs.p2.hitFlash > 0, gs.p2.boostTimer, gs.p2.slowTimer, gs.p2.paddleAngle, gs.p2.wakeParticles);

    drawLaneHUD(ctx, gs.p1, 0);
    drawLaneHUD(ctx, gs.p2, LANE_W);

    if (!gs.gameOver) rafRef.current = requestAnimationFrame(gameLoop);
  }, [checkHit, triggerQuiz, spawnObjects, endGame]);

  useEffect(() => {
    if (!started || result) return;
    timerRef.current = setInterval(() => {
      if (!stateRef.current) return;
      stateRef.current.timeLeft = Math.max(0, stateRef.current.timeLeft - 1);
      setTimeDisplay(stateRef.current.timeLeft);
      if (stateRef.current.timeLeft <= 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        endGame(stateRef.current);
      }
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [started, result, endGame]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      keysRef.current.add(e.key);
      const gs = stateRef.current;
      if (!gs) return;
      if (quiz1Ref.current && !quiz1Ref.current.answered && gs.p1.quizActive) {
        const isTrueFalse = quiz1Ref.current.question.type === 'true-false';
        if (isTrueFalse) {
          // Đúng/Sai: A = Đúng (0), W = Sai (1)
          const map: Record<string, number> = { a: 0, A: 0, w: 1, W: 1 };
          if (map[e.key] !== undefined) resolveQuiz(1, map[e.key]);
        } else {
          const map: Record<string, number> = { a: 0, A: 0, w: 1, W: 1, s: 2, S: 2, z: 3, Z: 3 };
          if (map[e.key] !== undefined) resolveQuiz(1, map[e.key]);
        }
      }
      if (quiz2Ref.current && !quiz2Ref.current.answered && gs.p2.quizActive) {
        const isTrueFalse = quiz2Ref.current.question.type === 'true-false';
        if (isTrueFalse) {
          // Đúng/Sai: J = Đúng (0), I = Sai (1)
          const map: Record<string, number> = { j: 0, J: 0, i: 1, I: 1 };
          if (map[e.key] !== undefined) resolveQuiz(2, map[e.key]);
        } else {
          const map: Record<string, number> = { j: 0, J: 0, i: 1, I: 1, k: 2, K: 2, m: 3, M: 3 };
          if (map[e.key] !== undefined) resolveQuiz(2, map[e.key]);
        }
      }
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) e.preventDefault();
    };
    const up = (e: KeyboardEvent) => keysRef.current.delete(e.key);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [resolveQuiz]);

  useEffect(() => {
    if (started) return;
    stateRef.current = initState();
    stateRef.current.running = false;
    const t = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(t);
          if (stateRef.current) stateRef.current.running = true;
          setStarted(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [initState]);

  useEffect(() => {
    if (!started) return;
    rafRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [started, gameLoop]);

  const handleRestart = () => {
    cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (quiz1TimerRef.current) clearInterval(quiz1TimerRef.current);
    if (quiz2TimerRef.current) clearInterval(quiz2TimerRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    quiz1Ref.current = null; quiz2Ref.current = null;
    setResult(null); setQuiz1Display(null); setQuiz2Display(null);
    setStarted(false); setCountdown(3); setTimeDisplay(GAME_DURATION);
    setScoreDisplay({ p1: 0, p2: 0 }); setLivesDisplay({ p1: MAX_LIVES, p2: MAX_LIVES });
  };

  const mins    = Math.floor(timeDisplay / 60);
  const secs    = timeDisplay % 60;
  const timerRed = timeDisplay <= 30;

  const renderHearts = (count: number, max: number) =>
    Array.from({ length: max }, (_, i) => (
      <span key={i} style={{ color: i < count ? '#F44336' : 'rgba(189,189,189,0.5)', fontSize: 15, lineHeight: 1 }}>♥</span>
    ));

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-2 px-2"
      style={{ background: 'linear-gradient(160deg, #E3F2FD 0%, #E8F5E9 100%)' }}>

      {/* Top bar */}
      <div className="w-full max-w-5xl flex items-center justify-between mb-2 px-1">
        <button onClick={onBack}
          className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900 cursor-pointer whitespace-nowrap text-sm font-bold bg-white/80 px-3 py-1.5 rounded-xl border border-slate-200">
          <i className="ri-arrow-left-line"></i> Quay lại
        </button>
        <div className="text-center">
          <div className="text-slate-800 font-black text-base tracking-wide">⛵ CHÈO THUYỀN ĐỘC MỘC</div>
          <div className="text-slate-500 text-xs">Đối kháng 2 người · 5 phút thi đấu</div>
        </div>
        <div className="w-20"></div>
      </div>

      {/* Score / Lives / Timer bar */}
      <div className="w-full max-w-5xl grid grid-cols-3 gap-2 mb-2">
        {/* Team 1 */}
        <div className="bg-gradient-to-br from-sky-50 to-blue-50 border-2 border-sky-300 rounded-2xl px-3 py-2">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-sky-700 font-black text-sm flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-sky-500 inline-block"></span> Đội Xanh
            </span>
            <div className="flex gap-0.5">{renderHearts(livesDisplay.p1, MAX_LIVES)}</div>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-sky-900 font-black text-2xl">{scoreDisplay.p1}</span>
            <span className="text-sky-400 text-xs">điểm</span>
          </div>
          <div className="text-sky-500 text-xs mt-0.5 font-medium">Di chuyển: A / D</div>
        </div>

        {/* Timer center */}
        <div className="bg-white border-2 border-amber-300 rounded-2xl px-3 py-2 flex flex-col items-center justify-center">
          <div className={`font-mono text-3xl font-black tracking-widest ${timerRed ? 'text-red-500 animate-pulse' : 'text-slate-800'}`}>
            {mins}:{secs.toString().padStart(2, '0')}
          </div>
          <div className="text-slate-400 text-xs mt-0.5">⏱ thời gian còn lại</div>
          <div className="flex gap-1 mt-1">
            {[1, 2, 3, 4].map(ph => {
              const phaseTime = ph === 1 ? 60 : ph === 2 ? 150 : ph === 3 ? 240 : 300;
              const active = GAME_DURATION - timeDisplay < phaseTime;
              return (
                <div key={ph}
                  className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${active ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'}`}>
                  GĐ{ph}
                </div>
              );
            })}
          </div>
        </div>

        {/* Team 2 */}
        <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-300 rounded-2xl px-3 py-2">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-red-700 font-black text-sm flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span> Đội Đỏ
            </span>
            <div className="flex gap-0.5">{renderHearts(livesDisplay.p2, MAX_LIVES)}</div>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-red-900 font-black text-2xl">{scoreDisplay.p2}</span>
            <span className="text-red-400 text-xs">điểm</span>
          </div>
          <div className="text-red-500 text-xs mt-0.5 font-medium">Di chuyển: ◄ / ►</div>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative w-full max-w-5xl rounded-3xl overflow-hidden border-4 border-white"
        style={{ aspectRatio: `${CANVAS_W}/${CANVAS_H}` }}>
        <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} className="w-full h-full block" />

        {/* Countdown */}
        {!started && countdown > 0 && (
          <div className="absolute inset-0 bg-white/75 flex flex-col items-center justify-center rounded-3xl">
            <div className="text-slate-700 text-xl font-bold mb-3">Chuẩn bị!</div>
            <div className="font-black text-slate-800" style={{ fontSize: 90 }}>{countdown}</div>
          </div>
        )}

        {/* P1 Quiz overlay */}
        {quiz1Display && (
          <div className="absolute top-0 left-0 w-1/2 h-full flex items-center justify-center"
            style={{ background: 'rgba(219,234,254,0.92)', backdropFilter: 'blur(4px)' }}>
            <QuizPanel quiz={quiz1Display} playerLabel="🔵 Đội Xanh" teamColor="blue" keyGuide="A=A  ·  W=B  ·  S=C  ·  Z=D" />
          </div>
        )}

        {/* P2 Quiz overlay */}
        {quiz2Display && (
          <div className="absolute top-0 right-0 w-1/2 h-full flex items-center justify-center"
            style={{ background: 'rgba(254,226,226,0.92)', backdropFilter: 'blur(4px)' }}>
            <QuizPanel quiz={quiz2Display} playerLabel="🔴 Đội Đỏ" teamColor="red" keyGuide="J=A  ·  I=B  ·  K=C  ·  M=D" />
          </div>
        )}
      </div>

      {/* Scoring info */}
      <div className="w-full max-w-5xl mt-2 bg-white/70 border border-slate-200 rounded-2xl px-4 py-2">
        <div className="flex flex-wrap justify-center gap-3 text-xs text-slate-600 font-medium">
          <span className="text-green-600">✅ Né chướng ngại vật (đá/gỗ/xoáy nước): +{DODGE_POINTS}đ</span>
          <span className="text-green-600">♻️ Nhặt rác (túi/chai/lon): trả lời đúng → +điểm + tăng tốc</span>
          <span className="text-red-500">💥 Va đá/cành/khúc gỗ: −8đ + lỗi</span>
          <span className="text-red-500">❌ Trả lời sai / hết giờ: −điểm + lỗi</span>
          <span className="text-orange-500">⚠️ 3 lỗi = −1 mạng · Hết mạng = Thua ngay</span>
          <span className="text-sky-600">🏆 Hết giờ: nhiều mạng hơn / điểm cao hơn thắng</span>
        </div>
      </div>

      {/* Result Modal */}
      {result && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: 'rgba(200,230,255,0.8)', backdropFilter: 'blur(8px)' }}>
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center border-4 border-amber-200">
            <div className="text-6xl mb-3">{result.winner === 'draw' ? '🤝' : '🏆'}</div>
            <h2 className="text-2xl font-black text-slate-800 mb-2">
              {result.winner === 'draw' ? 'Hai đội hòa!' : result.winner === 'p1' ? '🔵 Đội Xanh thắng!' : '🔴 Đội Đỏ thắng!'}
            </h2>
            <p className="text-slate-400 mb-5 text-sm">Kết thúc trận đấu!</p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`rounded-2xl p-4 ${result.winner === 'p1' ? 'bg-sky-50 border-2 border-sky-400' : 'bg-slate-50'}`}>
                <div className="text-2xl mb-1">🔵</div>
                <div className="text-2xl font-black text-sky-700">{result.p1Score} <span className="text-sm font-normal text-sky-400">điểm</span></div>
                <div className="text-sm text-slate-500 mt-1">{'♥'.repeat(result.p1Lives)}{'🖤'.repeat(MAX_LIVES - result.p1Lives)}</div>
              </div>
              <div className={`rounded-2xl p-4 ${result.winner === 'p2' ? 'bg-red-50 border-2 border-red-400' : 'bg-slate-50'}`}>
                <div className="text-2xl mb-1">🔴</div>
                <div className="text-2xl font-black text-red-600">{result.p2Score} <span className="text-sm font-normal text-red-400">điểm</span></div>
                <div className="text-sm text-slate-500 mt-1">{'♥'.repeat(result.p2Lives)}{'🖤'.repeat(MAX_LIVES - result.p2Lives)}</div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={handleRestart}
                className="w-full py-3 bg-gradient-to-r from-sky-500 to-cyan-400 text-white font-bold text-base rounded-2xl cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-restart-line"></i> Chơi lại
              </button>
              <button onClick={onBack}
                className="w-full py-2.5 bg-slate-100 text-slate-600 font-bold rounded-2xl cursor-pointer whitespace-nowrap text-sm">
                Về trang trò chơi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Quiz Panel Component ─── */
function QuizPanel({
  quiz, playerLabel, teamColor, keyGuide,
}: {
  quiz: QuizState;
  playerLabel: string;
  teamColor: 'blue' | 'red';
  keyGuide: string;
}) {
  const isTrueFalse = quiz.question.type === 'true-false';
  const LABELS = isTrueFalse ? ['✓ Đúng', '✗ Sai'] : ['A', 'B', 'C', 'D'];
  const isBlue = teamColor === 'blue';

  const headerBg   = isBlue ? 'bg-sky-600' : 'bg-red-500';
  const cardBorder = isBlue ? 'border-sky-400' : 'border-red-400';
  const timerColor = quiz.timeLeft <= 8 ? 'text-red-500' : isBlue ? 'text-sky-700' : 'text-red-700';
  const timerBg    = quiz.timeLeft <= 8 ? 'bg-red-50' : isBlue ? 'bg-sky-50' : 'bg-red-50';
  const progressColor = quiz.timeLeft <= 8 ? '#EF4444' : isBlue ? '#0284C7' : '#EF4444';
  const progressPct   = (quiz.timeLeft / QUIZ_DURATION) * 100;

  // Key guide based on question type
  const displayKeyGuide = isTrueFalse
    ? (isBlue ? 'A = Đúng  ·  W = Sai' : 'J = Đúng  ·  I = Sai')
    : keyGuide;

  const getOptionStyle = (i: number) => {
    if (!quiz.showResult) {
      if (isTrueFalse) {
        return i === 0
          ? (isBlue ? 'bg-green-50 border-green-300 hover:border-green-500' : 'bg-green-50 border-green-300 hover:border-green-500')
          : (isBlue ? 'bg-red-50 border-red-200 hover:border-red-400' : 'bg-red-50 border-red-200 hover:border-red-400');
      }
      return isBlue ? 'bg-sky-50 border-sky-200 hover:border-sky-400' : 'bg-red-50 border-red-200 hover:border-red-400';
    }
    if (i === quiz.question.correct) return 'bg-green-100 border-green-400 ring-2 ring-green-300';
    if (i === quiz.selectedIdx && i !== quiz.question.correct) return 'bg-red-100 border-red-400';
    return 'bg-slate-50 border-slate-200';
  };

  const options = quiz.question.options as string[];

  return (
    <div className={`bg-white rounded-2xl border-4 ${cardBorder} w-11/12 max-w-xs overflow-hidden`}
      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
      <div className={`${headerBg} px-3 py-2.5 flex items-center justify-between`}>
        <span className="text-white font-black text-sm">{playerLabel}</span>
        <div className={`${timerBg} rounded-xl px-2.5 py-0.5 flex items-center gap-1`}>
          <i className="ri-time-line text-xs" style={{ color: progressColor }}></i>
          <span className={`font-black text-base ${timerColor}`}>{quiz.timeLeft}s</span>
        </div>
      </div>

      <div className="h-1.5 bg-slate-100">
        <div className="h-full transition-all duration-500"
          style={{ width: `${progressPct}%`, background: progressColor }} />
      </div>

      <div className={`mx-3 mt-2.5 mb-1 px-3 py-1.5 rounded-xl text-center text-xs font-bold tracking-wide ${isBlue ? 'bg-sky-50 text-sky-700 border border-sky-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
        🎮 Bấm phím:&nbsp; <span className="font-mono">{displayKeyGuide}</span>
      </div>

      <div className="px-3 pt-1.5 pb-2">
        <div className={`text-xs font-bold mb-1.5 px-2 py-0.5 rounded-full inline-block ${isTrueFalse ? (isBlue ? 'bg-amber-100 text-amber-700' : 'bg-amber-100 text-amber-700') : (isBlue ? 'bg-sky-100 text-sky-600' : 'bg-red-100 text-red-600')}`}>
          {isTrueFalse ? '✅ Câu hỏi Đúng / Sai' : '♻️ Câu hỏi trắc nghiệm'}
        </div>
        <p className="text-slate-800 font-bold text-sm leading-snug min-h-[44px]">{quiz.question.text}</p>
      </div>

      <div className={`px-3 pb-2.5 ${isTrueFalse ? 'flex gap-2' : 'grid grid-cols-2 gap-1.5'}`}>
        {options.map((opt, i) => (
          <div key={i}
            className={`rounded-xl p-2 border-2 ${getOptionStyle(i)} relative ${isTrueFalse ? 'flex-1 text-center' : ''}`}>
            {quiz.showResult && i === quiz.question.correct && (
              <div className="absolute -top-2 -right-2 text-green-500 text-base leading-none">✓</div>
            )}
            {quiz.showResult && i === quiz.selectedIdx && i !== quiz.question.correct && (
              <div className="absolute -top-2 -right-2 text-red-500 text-base leading-none">✗</div>
            )}
            {isTrueFalse ? (
              <div className={`font-black text-sm ${i === 0 ? 'text-green-600' : 'text-red-500'}`}>{opt}</div>
            ) : (
              <div className="flex items-start gap-1">
                <span className={`font-black text-xs min-w-[14px] ${isBlue ? 'text-sky-600' : 'text-red-500'}`}>{LABELS[i]}.</span>
                <span className="text-slate-700 text-xs font-medium leading-tight">{opt}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {quiz.showResult && (
        <div className={`mx-3 mb-3 rounded-xl p-2.5 text-center font-bold text-xs ${quiz.isCorrect ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
          {quiz.isCorrect
            ? '🎉 Chính xác! Thuyền tăng tốc!'
            : `❌ Sai rồi! Đáp án đúng là ${isTrueFalse ? (quiz.question.correct === 0 ? 'Đúng' : 'Sai') : LABELS[quiz.question.correct]}`}
        </div>
      )}
    </div>
  );
}
